import { cssVars } from '@/app/styles';
import { BookedRatioHeader } from '@/card/components/booked-ratio-header';
import { DataUnavailableHeader } from '@/card/components/data-unavailable-header';
import * as header from '@/card/components/header-styles';
import { KpiBookingActivities } from '@/card/components/kpi-booking-activities';
import { KpiDefaultSm } from '@/card/components/kpi-default-sm';
import { KpiOnlinePlatforms } from '@/card/components/kpi-online-platforms';
import { KpiRibbonSm } from '@/card/components/kpi-ribbon-sm';
import {
  dataTypeDescription,
  dataTypeKeyNameMap,
  datasetTypeLabels,
  datasetTypeSymbols,
  datasetTypes,
  subtypeChartLabels,
} from '@/constants';
import * as dataSelectors from '@/data/data-selectors';
import { useStringTranslatorFunction } from '@/translations/hooks';
import { connect } from 'react-redux';

/**
 * Latest kpi is not always the last point in a set. It should be the last point of
 * actual data in a set. Here we traverse backwards until we find the first
 * non null or non-undefined value or we return null;
 * @param {string} dataType
 * @param {object} [kpiData]
 * @param {object[]} [kpiData.dataPoints]
 * @param {object} [kpiData.latestKpi]
 * @returns
 */
function extractLatestKpi(dataType, kpiData) {
  const { dataPoints = null, latestKpi = null } = kpiData;
  const n = dataPoints?.length ?? 0;

  // If latestKpi data is supplied then we should use that instead of the last point in the data set.
  if (!!latestKpi?.[dataType]) {
    return {
      latestKpi: latestKpi[dataType],
      priorPoint: dataPoints?.[n - 2] ?? null,
    };
  }

  const dps = [];

  // Keep searching backwards until we have at least 2 data points.
  for (let i = n - 1; i >= 0; i--) {
    if (!!dataPoints[i]?.[dataType]) {
      dps.push(dataPoints[i]);
    }

    if (dps.length === 2) {
      break;
    }
  }

  const [lastKpi, priorPoint] = dps;
  return { latestKpi: lastKpi?.[dataType] ?? null, priorPoint };
}

/**
 * Performs a sum calculation across all supplied data points.
 *
 * @param {object} propertyData
 * @param {any[]} propertyData.dataPoints
 * @param {string} dataType
 * @returns {number} A sum of kpi values
 */
function sumKpi(dataPoints, dataType) {
  return (
    dataPoints?.reduce((sum, dp) => sum + (dp?.[dataType]?.value ?? 0), 0) ?? 0
  );
}

/**
 * This function wraps kpiribbonsm to fill it with data.
 * @param {*} props props to fill and style header
 */
function SingleValueHeader(props) {
  const { children, ...rest } = props;
  return <KpiRibbonSm {...rest}>{children}</KpiRibbonSm>;
}

/**
 * Wraps the kpidefaultsm header to fill and style.
 * @param {*} props to fill and style.
 */
function MultiValueHeader(props) {
  const {
    latestKpi,
    label,
    labelFontSize,
    valueFontSize,
    dataType,
    marginUnderLabel,
  } = props;
  return (
    <KpiDefaultSm
      label={label}
      marginUnderLabel={marginUnderLabel}
      labelFontSize={labelFontSize}
      valueFontSize={valueFontSize}
      labels={subtypeChartLabels[dataType]}
      colors={cssVars.color.subtype[dataType]}
      latestKpi={latestKpi}
      houseIcon={dataType === dataTypeKeyNameMap.RENTAL_SIZE}
    />
  );
}

/**
 * Similar to Multi value header except it aggregates the lowest valued online platforms
 * into unknown. TODO: Consider adapting the multi value header to handle this
 */
function OnlinePlatformsHeader(props) {
  const { latestKpi, marginUnderLabel } = props;
  return (
    <KpiOnlinePlatforms
      marginUnderLabel={marginUnderLabel}
      latestKpi={latestKpi}
    />
  );
}

// Required for the TOT indicators
function ColoredDotWithTitle(props) {
  const { backgroundColor, title } = props;
  return (
    <header.ColoredDotWrapper>
      <header.StyledDot backgroundColor={backgroundColor} />
      <header.StyledDotTitle>{title}</header.StyledDotTitle>
    </header.ColoredDotWrapper>
  );
}

/**
 * Indicates Estimated vs actual TOT by color
 * Fed as child prop to single value header
 */
function EstimatedActualIndicator(props) {
  const { abbreviateIndicators, enableActual } = props;
  const estimatedTitle = abbreviateIndicators ? 'Est.' : 'Estimated';
  const actualTitle = abbreviateIndicators ? 'Act.' : 'Actual';
  return (
    <header.FlexRowCenter>
      <ColoredDotWithTitle backgroundColor="#00bfa5" title={estimatedTitle} />
      {enableActual && (
        <ColoredDotWithTitle backgroundColor="#fdb147" title={actualTitle} />
      )}
    </header.FlexRowCenter>
  );
}

/**
 * Special TOT Header b/c we calculate the header data on the fly
 * TODO: We need to get the data STR Oracle API asap
 */
function TOTHeader(props) {
  const { abbreviateIndicators, additionalHeaderOptions, ...rest } = props;

  return (
    <SingleValueHeader {...rest}>
      <EstimatedActualIndicator
        abbreviateIndicators={abbreviateIndicators}
        enableActual={additionalHeaderOptions?.enableActualTOT}
      />
    </SingleValueHeader>
  );
}

/**
 * Custom Header for Occupancy which displays the last kpi value as well as the total of nights booked.
 */
function OccupancyHeader(props) {
  const { dataPoints, ...rest } = props;
  const translateString = useStringTranslatorFunction();

  // Occupancy needs to sum the data from a different KPI key (ie. BOOKED) rather then the one supplied via 'dataType' property.
  const hasBooked = dataPoints.some((dp) => !!dp?.['booked']);
  const totalBooked = sumKpi(dataPoints, 'booked');

  return (
    <KpiBookingActivities
      showTotalBooked={hasBooked}
      totalBooked={totalBooked}
      totalBookedDescription={translateString(
        dataTypeDescription[datasetTypes.BOOKED]
      )}
      {...rest}
    />
  );
}

// We have 5 different headers. TODO: When this grows out of control and it will. Make these OOP.
const HEADER_TYPES = {
  SINGLE_VALUE: SingleValueHeader,
  MULTI_VALUE: MultiValueHeader,
  ONLINE_PLATFORMS: OnlinePlatformsHeader,
  BOOKED_RATIO: BookedRatioHeader,
  TRANSIENT_OCCUPANCY_TAX: TOTHeader,
  OCCUPANCY: OccupancyHeader,
};

// Maps chart to header type
const HEADER_BY_CHART = {
  CURRENT_STR_PROPERTIES: HEADER_TYPES.SINGLE_VALUE,
  REVENUE: HEADER_TYPES.SINGLE_VALUE,
  AVG_DAILY_RATE: HEADER_TYPES.SINGLE_VALUE,
  OCCUPANCY: HEADER_TYPES.OCCUPANCY,
  RESIDENTIAL_TYPE: HEADER_TYPES.MULTI_VALUE,
  RENTAL_STRUCTURE: HEADER_TYPES.MULTI_VALUE,
  UNIT_TYPE: HEADER_TYPES.MULTI_VALUE,
  RENTAL_SIZE: HEADER_TYPES.MULTI_VALUE,
  ONLINE_PLATFORMS: HEADER_TYPES.ONLINE_PLATFORMS,
  BOOKED_RATIO: HEADER_TYPES.BOOKED_RATIO,
  TRANSIENT_OCCUPANCY_TAX: HEADER_TYPES.TRANSIENT_OCCUPANCY_TAX,
};

/**
 * The header factory takes in every possible header prop.
 * based on the props, it is able to determine, where to get the data, which header to use,
 * and which header style to apply. Header factory returns the correct chart header as result.
 * @param {*} props All possible header props
 */
const HeaderFactory = (props) => {
  const {
    kpiData,
    propertyData,
    chartName,
    expand,
    abbreviateIndicators,
    marginUnderLabel,
    labelFontSize,
    valueFontSize,
    enableTrend,
    additionalHeaderOptions,
  } = props;

  const Header = HEADER_BY_CHART[chartName];
  const translateString = useStringTranslatorFunction();

  const label = translateString(datasetTypeLabels[chartName]);

  const kpiDescription = translateString(dataTypeDescription[chartName]);

  const dataType = dataTypeKeyNameMap[chartName];
  const { latestKpi, priorPoint } = extractLatestKpi(
    dataType,
    propertyData ?? kpiData
  );

  if (!latestKpi && chartName !== 'BOOKED_RATIO') {
    // Booked Ratio is just a dummy header
    return <DataUnavailableHeader label={label} />;
  }

  return (
    chartName && (
      <Header
        abbreviateIndicators={abbreviateIndicators}
        marginUnderLabel={marginUnderLabel}
        enableTrend={enableTrend}
        expand={expand}
        priorPoint={priorPoint}
        latestKpi={latestKpi}
        dataType={dataType}
        label={label}
        labelFontSize={labelFontSize}
        valueFontSize={valueFontSize}
        latestKpiDescription={kpiDescription}
        dataPoints={propertyData?.dataPoints ?? kpiData?.dataPoints}
        symbol={datasetTypeSymbols[chartName]}
        additionalHeaderOptions={additionalHeaderOptions}
      />
    )
  );
};

const mapStateToProps = (state) => ({
  propertyList: dataSelectors.propertyList(state),
  kpiData: dataSelectors.kpiData(state),
});

const ChartHeader = connect(mapStateToProps, null)(HeaderFactory);

// TODO: Do not export as default. Can't find where it's used easily
export default ChartHeader;
